"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { HiCalculator, HiChartBar, HiQuestionMarkCircle, HiUsers } from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { instagramEngagementRateCalculatorFAQ } from "@/lib/data/faq/instagram-engagement-rate-calculator";
import { faqToFAQPageSchema } from "@/lib/schemas/faq-schemas";
import JSONLDSchema from "@/components/JSONLDSchema";

/** Benchmark bands for engagement rate by followers, keyed by account size. */
const BENCHMARKS = [
  { maxFollowers: 10_000, label: "Nano (under 10K)", typical: "4-6%", strong: 6 },
  { maxFollowers: 100_000, label: "Micro (10K-100K)", typical: "2-4%", strong: 4 },
  { maxFollowers: 1_000_000, label: "Macro (100K-1M)", typical: "1.5-3%", strong: 3 },
  { maxFollowers: Infinity, label: "Mega (1M+)", typical: "1-2%", strong: 2 },
];

function toNumber(value: string): number {
  const n = parseFloat(value.replace(/,/g, ""));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export default function InstagramEngagementRateCalculatorClient() {
  const [followers, setFollowers] = useState("10000");
  const [likes, setLikes] = useState("400");
  const [comments, setComments] = useState("20");
  const [saves, setSaves] = useState("");
  const [shares, setShares] = useState("");
  const [reach, setReach] = useState("");

  const result = useMemo(() => {
    const f = toNumber(followers);
    const interactions = toNumber(likes) + toNumber(comments) + toNumber(saves) + toNumber(shares);
    const r = toNumber(reach);

    const byFollowers = f > 0 ? (interactions / f) * 100 : 0;
    const byReach = r > 0 ? (interactions / r) * 100 : 0;

    const band = BENCHMARKS.find((b) => f <= b.maxFollowers) ?? BENCHMARKS[BENCHMARKS.length - 1];

    let verdict = "Add your numbers to see how you compare.";
    let tone: "good" | "ok" | "low" | "none" = "none";
    if (f > 0 && interactions > 0) {
      if (byFollowers >= band.strong) {
        verdict = `Strong. That's above the typical ${band.typical} range for ${band.label} accounts.`;
        tone = "good";
      } else if (byFollowers >= 1) {
        verdict = `Healthy. Typical for ${band.label} accounts is around ${band.typical}.`;
        tone = "ok";
      } else {
        verdict = `Below average. ${band.label} accounts typically see ${band.typical}. Inactive followers are a common cause.`;
        tone = "low";
      }
    }

    return { byFollowers, byReach, interactions, band, verdict, tone, hasReach: r > 0 };
  }, [followers, likes, comments, saves, shares, reach]);

  const toneClasses =
    result.tone === "good"
      ? "text-green-600 dark:text-green-400"
      : result.tone === "low"
        ? "text-amber-600 dark:text-amber-400"
        : "text-foreground";

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Instagram Engagement Rate Calculator"
        description="Calculate your Instagram engagement rate by followers or by reach, and compare it against benchmarks for your account size."
        url="https://plugmonkey.xyz/tool/instagram-engagement-rate-calculator/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Instagram Engagement Rate Calculator",
            url: "https://plugmonkey.xyz/tool/instagram-engagement-rate-calculator/",
          },
        ]}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-primary/10 rounded-full text-primary">
          <HiCalculator className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Instagram Engagement Rate Calculator</h1>
          <p className="text-muted-foreground mt-1">
            Work out your true engagement rate and see how it compares.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Engagement rate is the number brands actually check when they evaluate an account, and it
          matters far more than follower count. Enter your followers and your average likes and
          comments to get your rate instantly, along with a benchmark for accounts your size.
        </p>
        <p>
          Saves, shares, and reach are optional. Add them if you have Instagram Insights open —
          saves and shares carry real weight with the algorithm, and reach gives you a more honest
          read on how your content performed with the people who actually saw it.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <HiUsers className="w-5 h-5 text-primary" />
              Your numbers
            </CardTitle>
            <CardDescription>Use averages from your last 9-12 posts.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="followers">Followers</Label>
              <Input
                id="followers"
                inputMode="numeric"
                value={followers}
                onChange={(e) => setFollowers(e.target.value)}
                placeholder="10000"
              />
            </div>
            <div>
              <Label htmlFor="likes">Average likes per post</Label>
              <Input
                id="likes"
                inputMode="numeric"
                value={likes}
                onChange={(e) => setLikes(e.target.value)}
                placeholder="400"
              />
            </div>
            <div>
              <Label htmlFor="comments">Average comments per post</Label>
              <Input
                id="comments"
                inputMode="numeric"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="20"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <HiChartBar className="w-5 h-5 text-primary" />
              Optional (from Insights)
            </CardTitle>
            <CardDescription>Only you can see these — they sharpen the result.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="saves">Average saves per post</Label>
              <Input
                id="saves"
                inputMode="numeric"
                value={saves}
                onChange={(e) => setSaves(e.target.value)}
                placeholder="optional"
              />
            </div>
            <div>
              <Label htmlFor="shares">Average shares per post</Label>
              <Input
                id="shares"
                inputMode="numeric"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
                placeholder="optional"
              />
            </div>
            <div>
              <Label htmlFor="reach">Average reach per post</Label>
              <Input
                id="reach"
                inputMode="numeric"
                value={reach}
                onChange={(e) => setReach(e.target.value)}
                placeholder="optional"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-12 border-primary/30">
        <CardHeader>
          <CardTitle className="text-lg">Your engagement rate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-muted-foreground mb-1">By followers</p>
              <p className={`text-4xl font-bold tabular-nums ${toneClasses}`}>
                {result.byFollowers.toFixed(2)}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {result.interactions.toLocaleString()} interactions per post
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">By reach</p>
              <p className="text-4xl font-bold tabular-nums text-foreground">
                {result.hasReach ? `${result.byReach.toFixed(2)}%` : "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {result.hasReach ? "Based on the reach you entered" : "Add reach to calculate"}
              </p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-border/50">
            <p className="text-sm font-medium">{result.verdict}</p>
            <p className="text-xs text-muted-foreground mt-2">
              Benchmarks are general industry ranges, not official Instagram figures. Compare
              against accounts of a similar size in your niche for the most useful read.
            </p>
          </div>
        </CardContent>
      </Card>

      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-4">How the formula works</h2>
        <div className="text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
          <p>
            The standard formula is{" "}
            <strong className="text-foreground">(likes + comments) ÷ followers × 100</strong>. This
            calculator adds saves and shares to the interaction count when you supply them, because
            Instagram weights those signals heavily and leaving them out understates how well a post
            actually performed.
          </p>
          <p>
            Engagement rate by followers is what brands and influencer platforms use, since
            followers are the only number visible from outside an account. Engagement rate by reach
            is the more honest measure of content quality — it tells you what share of the people
            who actually saw the post did something about it — but it needs Insights data only the
            owner can see.
          </p>
          <p>
            One important caveat: every inactive or fake follower inflates the denominator without
            ever contributing an interaction. If your rate looks low despite solid absolute numbers,
            a bloated follower list is usually the reason.
          </p>
        </div>
      </section>

      <section className="mb-12 rounded-2xl border border-border bg-card p-8">
        <h2 className="text-xl font-bold mb-3">Low rate? Check your follower quality first</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          Before rewriting your content strategy, find out how much of your audience is real. Ghost
          accounts and inactive followers drag the ratio down no matter how good a post is.{" "}
          <Link
            href="/product/instagram-followers-exporter-pro/"
            className="text-primary underline"
          >
            Followers Exporter Pro for Instagram
          </Link>{" "}
          exports your follower list to CSV with follower counts, bios, and verified status so you
          can audit it in a spreadsheet, and{" "}
          <Link href="/product/instagram-unfollow-pro/" className="text-primary underline">
            Unfollow Pro for Instagram
          </Link>{" "}
          helps you clear out the accounts that never engage.
        </p>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link
            href="/guide/identify-fake-followers-instagram/"
            className="text-primary underline underline-offset-2"
          >
            How to identify fake followers
          </Link>
          <span className="text-muted-foreground">·</span>
          <Link
            href="/guide/find-ghost-followers-instagram/"
            className="text-primary underline underline-offset-2"
          >
            How to find ghost followers
          </Link>
        </div>
      </section>

      <ToolsCustomDevCTA />

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={instagramEngagementRateCalculatorFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      {/* FAQ Schema */}
      {(() => {
        const faqSchema = faqToFAQPageSchema(instagramEngagementRateCalculatorFAQ, {
          url: "https://plugmonkey.xyz/tool/instagram-engagement-rate-calculator/",
          name: "Instagram Engagement Rate Calculator - Frequently Asked Questions",
          description:
            "Answers to common questions about calculating Instagram engagement rate, benchmarks by account size, and why inactive followers lower your rate.",
        });
        return faqSchema ? <JSONLDSchema schema={faqSchema} /> : null;
      })()}
    </div>
  );
}
